import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./config/apiConfig";
import "./MonitoringPage.css";

const MonitoringPage = ({ user, logout }) => {
  const navigate = useNavigate();
  const [activeJobs, setActiveJobs] = useState([]);
  const [jobHistory, setJobHistory] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filter, setFilter] = useState("all");

  // RSS Feed Tracking State
  const [rssFeeds, setRssFeeds] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [feedProcessingTimes, setFeedProcessingTimes] = useState({});
  const [showRssTracking, setShowRssTracking] = useState(false);

  const logsEndRef = useRef(null);
  const refreshInterval = useRef(null);
  const rssTrackingInterval = useRef(null);

  // Auto-scroll to bottom of logs
  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [systemLogs, webhookLogs]);

  // Fetch RSS feed configurations
  const fetchRSSFeeds = async () => {
    try {
      const response = await apiFetch("/admin/rss/feeds");

      if (response.ok) {
        const feeds = await response.json();
        setRssFeeds(feeds);
      }
    } catch (error) {
      console.error("Error fetching RSS feeds:", error);
    }
  };

  // Fetch real-time RSS processing status
  const fetchRSSProcessingStatus = async () => {
    try {
      const response = await apiFetch("/admin/rss/processing-status");

      if (response.ok) {
        const status = await response.json();

        if (status.isProcessing) {
          setCurrentBatch(status.currentBatch);
          setShowRssTracking(true);

          // Update feed processing times
          if (status.feedStatuses) {
            const newTimes = { ...feedProcessingTimes };
            status.feedStatuses.forEach((feed) => {
              if (feed.status === "processing" && !newTimes[feed.url]) {
                newTimes[feed.url] = Date.now();
              } else if (
                feed.status === "completed" ||
                feed.status === "failed"
              ) {
                delete newTimes[feed.url];
              }
            });
            setFeedProcessingTimes(newTimes);
          }

          // Update RSS feeds with current status
          setRssFeeds((prevFeeds) =>
            prevFeeds.map((feed) => {
              const statusUpdate = status.feedStatuses?.find(
                (s) => s.url === feed.url
              );
              if (statusUpdate) {
                return {
                  ...feed,
                  status: statusUpdate.status,
                  error: statusUpdate.error,
                  articles: statusUpdate.articles,
                  responseTime: statusUpdate.responseTime,
                  lastProcessed: statusUpdate.lastProcessed,
                };
              }
              return feed;
            })
          );
        } else {
          setShowRssTracking(false);
          setCurrentBatch(null);
          setFeedProcessingTimes({});
        }
      }
    } catch (error) {
      console.error("Error fetching RSS processing status:", error);
    }
  };

  // Calculate how long a feed has been processing
  const getFeedProcessingDuration = (feedUrl) => {
    const startTime = feedProcessingTimes[feedUrl];
    if (!startTime) return 0;
    return Date.now() - startTime;
  };

  // Check if a feed is hanging (processing for too long)
  const isFeedHanging = (feedUrl) => {
    const duration = getFeedProcessingDuration(feedUrl);
    return duration > 30000; // 30 seconds threshold
  };

  // Fetch active jobs
  const fetchActiveJobs = async () => {
    try {
      const response = await apiFetch("/admin/jobs/active");

      if (response.ok) {
        const jobs = await response.json();
        setActiveJobs(jobs);

        // Check if any RSS jobs are running to enable RSS tracking
        const rssJob = jobs.find(
          (job) =>
            job.operation === "RSS Aggregation" && job.status === "running"
        );

        if (rssJob && !showRssTracking) {
          setShowRssTracking(true);
          fetchRSSFeeds();
        } else if (!rssJob && showRssTracking) {
          setShowRssTracking(false);
          setCurrentBatch(null);
          setFeedProcessingTimes({});
        }
      }
    } catch (error) {}
  };

  // Fetch job history
  const fetchJobHistory = async () => {
    try {
      const response = await apiFetch("/admin/jobs/history?limit=50");
      if (response.ok) {
        const history = await response.json();
        setJobHistory(history);
      }
    } catch (error) {}
  };

  // Fetch webhook logs
  const fetchWebhookLogs = async () => {
    try {
      const response = await apiFetch("/admin/webhooks/logs?limit=100");
      if (response.ok) {
        const logs = await response.json();
        setWebhookLogs(logs);
      }
    } catch (error) {}
  };

  // Fetch system logs (simulated - in real implementation this would come from Cloudflare)
  const fetchSystemLogs = async () => {
    try {
      const response = await apiFetch("/admin/logs/recent?limit=200");
      if (response.ok) {
        const logs = await response.json();
        setSystemLogs(logs);
      }
    } catch (error) {}
  };

  // Trigger RSS aggregation with webhook monitoring
  const triggerRSSAggregation = async () => {
    try {
      const response = await apiFetch("/admin/rss-aggregation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          batchMode: true,
          webhookUrl: `${window.location.origin.replace("notmyview.com", "api.notmyview.com")}/webhooks/rss-complete`,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        addSystemLog("info", `RSS Aggregation triggered: ${result.message}`);

        if (result.jobId) {
          addSystemLog(
            "info",
            `Job ID: ${result.jobId} - Background processing started`
          );
        }

        // Start monitoring RSS processing
        setShowRssTracking(true);
        fetchRSSFeeds();

        // Refresh active jobs to show the new job
        setTimeout(fetchActiveJobs, 1000);
      } else {
        const error = await response.json();
        addSystemLog(
          "error",
          `Failed to trigger RSS aggregation: ${error.error}`
        );
      }
    } catch (error) {
      addSystemLog(
        "error",
        `Error triggering RSS aggregation: ${error.message}`
      );
    }
  };

  // Add log entry
  const addSystemLog = (level, message) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      source: "admin-ui",
    };
    setSystemLogs((prev) => [...prev.slice(-199), logEntry]);
  };

  // Get job details
  const getJobDetails = async (jobId) => {
    try {
      const response = await apiFetch(`/admin/jobs/${jobId}`);
      if (response.ok) {
        const job = await response.json();
        setSelectedJob(job);
      }
    } catch (error) {
      addSystemLog("error", `Error fetching job details: ${error.message}`);
    }
  };

  // Cancel job
  const cancelJob = async (jobId) => {
    try {
      const response = await apiFetch(`/admin/jobs/${jobId}/cancel`, {
        method: "POST",
      });

      if (response.ok) {
        addSystemLog("info", `Job ${jobId} cancelled successfully`);
        fetchActiveJobs();
        fetchJobHistory();
      } else {
        const error = await response.json();
        addSystemLog("error", `Failed to cancel job: ${error.error}`);
      }
    } catch (error) {
      addSystemLog("error", `Error cancelling job: ${error.message}`);
    }
  };

  // Setup auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        fetchActiveJobs();
        fetchJobHistory();
        fetchWebhookLogs();
        fetchSystemLogs();
      }, 5000); // Refresh every 5 seconds
    } else {
      clearInterval(refreshInterval.current);
    }

    return () => clearInterval(refreshInterval.current);
  }, [autoRefresh]);

  // Setup RSS tracking refresh (more frequent when RSS is running)
  useEffect(() => {
    if (showRssTracking && autoRefresh) {
      rssTrackingInterval.current = setInterval(() => {
        fetchRSSProcessingStatus();
      }, 2000); // Refresh every 2 seconds for RSS tracking
    } else {
      clearInterval(rssTrackingInterval.current);
    }

    return () => clearInterval(rssTrackingInterval.current);
  }, [showRssTracking, autoRefresh]);

  // Initial data fetch
  useEffect(() => {
    fetchActiveJobs();
    fetchJobHistory();
    fetchWebhookLogs();
    fetchSystemLogs();
    fetchRSSFeeds();
    setIsConnected(true);
  }, []);

  // Filter logs
  const filteredLogs = systemLogs.filter((log) => {
    if (filter === "all") return true;
    return log.level === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "running":
      case "processing":
        return "#2196F3";
      case "completed":
        return "#4CAF50";
      case "failed":
        return "#F44336";
      case "cancelled":
        return "#FF9800";
      case "pending":
        return "#9E9E9E";
      case "graceful_failure":
        return "#FF9800";
      default:
        return "#9E9E9E";
    }
  };

  const formatDuration = (start, end) => {
    if (!end) return "Running...";
    const duration = new Date(end) - new Date(start);
    return `${Math.round(duration / 1000)}s`;
  };

  const formatProcessingTime = (milliseconds) => {
    if (milliseconds < 1000) return `${milliseconds}ms`;
    return `${(milliseconds / 1000).toFixed(1)}s`;
  };

  // Group feeds by batch for display
  const groupFeedsByBatch = () => {
    const batchSize = 10; // RSS_BATCH_SIZE from backend
    const batches = [];

    for (let i = 0; i < rssFeeds.length; i += batchSize) {
      batches.push({
        batchNumber: Math.floor(i / batchSize) + 1,
        feeds: rssFeeds.slice(i, i + batchSize),
      });
    }

    return batches;
  };

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div className="header-content">
          <h1>System Monitoring</h1>
          <div className="header-navigation">
            <button onClick={() => navigate("/admin")} className="nav-btn">
              ← Admin Dashboard
            </button>
            <button onClick={() => navigate("/admin/rss")} className="nav-btn">
              ⚙️ RSS Management
            </button>
          </div>
        </div>
        <div className="monitoring-controls">
          <div className="connection-status">
            <span
              className={`status-indicator ${isConnected ? "connected" : "disconnected"}`}
            ></span>
            {isConnected ? "Connected" : "Disconnected"}
          </div>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button className="trigger-rss-btn" onClick={triggerRSSAggregation}>
            🔄 Trigger RSS Aggregation
          </button>
        </div>
      </div>

      <div
        className={`monitoring-grid ${showRssTracking ? "with-rss-tracking" : ""}`}
      >
        {/* RSS Feed Tracking Panel - Only show when RSS is running */}
        {showRssTracking && (
          <div className="monitoring-panel rss-tracking-panel">
            <div className="rss-tracking-header">
              <h2>RSS Feed Processing</h2>
              {currentBatch && (
                <div className="batch-info">
                  <span className="batch-indicator">
                    Batch {currentBatch.current}/{currentBatch.total}
                  </span>
                  <span className="batch-progress">
                    {Math.round(
                      (currentBatch.current / currentBatch.total) * 100
                    )}
                    %
                  </span>
                </div>
              )}
            </div>
            <div className="rss-feeds-container">
              {groupFeedsByBatch().map((batch) => (
                <div
                  key={batch.batchNumber}
                  className={`batch-group ${
                    currentBatch && batch.batchNumber === currentBatch.current
                      ? "active-batch"
                      : ""
                  } ${
                    currentBatch && batch.batchNumber < currentBatch.current
                      ? "completed-batch"
                      : ""
                  }`}
                >
                  <div className="batch-header">
                    <h4>Batch {batch.batchNumber}</h4>
                    <span className="batch-status">
                      {currentBatch &&
                      batch.batchNumber === currentBatch.current
                        ? "Processing"
                        : currentBatch &&
                            batch.batchNumber < currentBatch.current
                          ? "Completed"
                          : "Pending"}
                    </span>
                  </div>
                  <div className="feeds-list">
                    {batch.feeds.map((feed, index) => {
                      const processingDuration = getFeedProcessingDuration(
                        feed.url
                      );
                      const isHanging = isFeedHanging(feed.url);

                      return (
                        <div
                          key={feed.url}
                          className={`feed-item ${feed.status || "pending"} ${
                            isHanging ? "hanging" : ""
                          }`}
                        >
                          <div className="feed-info">
                            <div className="feed-name">{feed.name}</div>
                            <div className="feed-url">{feed.url}</div>
                          </div>
                          <div className="feed-status">
                            <span
                              className="status-badge"
                              style={{
                                backgroundColor: getStatusColor(
                                  feed.status || "pending"
                                ),
                              }}
                            >
                              {feed.status || "pending"}
                            </span>
                            {processingDuration > 0 && (
                              <span
                                className={`processing-time ${isHanging ? "hanging" : ""}`}
                              >
                                {formatProcessingTime(processingDuration)}
                                {isHanging && " ⚠️"}
                              </span>
                            )}
                            {feed.responseTime && (
                              <span className="response-time">
                                {formatProcessingTime(feed.responseTime)}
                              </span>
                            )}
                          </div>
                          <div className="feed-results">
                            {feed.articles && (
                              <span className="articles-count">
                                📰 {feed.articles} articles
                              </span>
                            )}
                            {feed.error && (
                              <span
                                className="error-message"
                                title={feed.error}
                              >
                                ❌ {feed.error.substring(0, 50)}...
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Jobs Panel */}
        <div className="monitoring-panel">
          <h2>Active Jobs ({activeJobs.length})</h2>
          <div className="jobs-list">
            {activeJobs.length === 0 ? (
              <div className="no-jobs">No active jobs</div>
            ) : (
              activeJobs.map((job) => (
                <div key={job.jobId} className="job-card active">
                  <div className="job-header">
                    <span className="job-id">{job.jobId}</span>
                    <span
                      className="job-status"
                      style={{ color: getStatusColor(job.status) }}
                    >
                      {job.status}
                    </span>
                  </div>
                  <div className="job-details">
                    <div className="job-operation">
                      {job.operation || "RSS Aggregation"}
                    </div>
                    <div className="job-progress">
                      {job.progress && (
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${job.progress}%` }}
                          ></div>
                          <span className="progress-text">{job.progress}%</span>
                        </div>
                      )}
                    </div>
                    <div className="job-message">{job.message}</div>
                    <div className="job-meta">
                      Started: {new Date(job.updatedAt).toLocaleTimeString()}
                      {job.batch &&
                        ` | Batch: ${job.batch}/${job.totalBatches}`}
                    </div>
                  </div>
                  <div className="job-actions">
                    <button
                      onClick={() => getJobDetails(job.jobId)}
                      className="btn-secondary"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => cancelJob(job.jobId)}
                      className="btn-danger"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Job History Panel */}
        <div className="monitoring-panel">
          <h2>Recent Jobs</h2>
          <div className="jobs-list">
            {jobHistory.slice(0, 10).map((job) => (
              <div key={job.jobId} className="job-card history">
                <div className="job-header">
                  <span className="job-id">{job.jobId}</span>
                  <span
                    className="job-status"
                    style={{ color: getStatusColor(job.status) }}
                  >
                    {job.status}
                  </span>
                </div>
                <div className="job-details">
                  <div className="job-operation">
                    {job.operation || "RSS Aggregation"}
                  </div>
                  <div className="job-meta">
                    {formatDuration(job.startedAt, job.completedAt)} |
                    {new Date(job.startedAt).toLocaleString()}
                  </div>
                  {job.articlesProcessed && (
                    <div className="job-results">
                      📰 {job.articlesProcessed} articles | 📚{" "}
                      {job.storiesCreated} stories
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Webhook Logs Panel */}
        <div className="monitoring-panel">
          <h2>Webhook Activity</h2>
          <div className="logs-container">
            {webhookLogs.slice(-20).map((log, index) => (
              <div key={index} className={`log-entry webhook-${log.status}`}>
                <span className="log-timestamp">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className="log-job">{log.jobId}</span>
                <span className="log-status">{log.status}</span>
                <span className="log-message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>

        {/* System Logs Panel */}
        <div className="monitoring-panel logs-panel">
          <div className="logs-header">
            <h2>System Logs</h2>
            <div className="log-filters">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Logs</option>
                <option value="info">Info</option>
                <option value="warn">Warnings</option>
                <option value="error">Errors</option>
              </select>
              <button
                onClick={() => setSystemLogs([])}
                className="clear-logs-btn"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="logs-container">
            {filteredLogs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.level}`}>
                <span className="log-timestamp">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className="log-level">{log.level.toUpperCase()}</span>
                <span className="log-message">{log.message}</span>
                {log.source && (
                  <span className="log-source">[{log.source}]</span>
                )}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Job Details: {selectedJob.jobId}</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedJob(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="job-detail-grid">
                <div className="detail-item">
                  <label>Status:</label>
                  <span style={{ color: getStatusColor(selectedJob.status) }}>
                    {selectedJob.status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Operation:</label>
                  <span>{selectedJob.operation}</span>
                </div>
                <div className="detail-item">
                  <label>Started:</label>
                  <span>
                    {new Date(selectedJob.startedAt).toLocaleString()}
                  </span>
                </div>
                {selectedJob.completedAt && (
                  <div className="detail-item">
                    <label>Completed:</label>
                    <span>
                      {new Date(selectedJob.completedAt).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <label>Duration:</label>
                  <span>
                    {formatDuration(
                      selectedJob.startedAt,
                      selectedJob.completedAt
                    )}
                  </span>
                </div>
                {selectedJob.progress && (
                  <div className="detail-item">
                    <label>Progress:</label>
                    <span>{selectedJob.progress}%</span>
                  </div>
                )}
                {selectedJob.articlesProcessed && (
                  <div className="detail-item">
                    <label>Articles Processed:</label>
                    <span>{selectedJob.articlesProcessed}</span>
                  </div>
                )}
                {selectedJob.storiesCreated && (
                  <div className="detail-item">
                    <label>Stories Created:</label>
                    <span>{selectedJob.storiesCreated}</span>
                  </div>
                )}
                {selectedJob.error && (
                  <div className="detail-item error">
                    <label>Error:</label>
                    <span>{selectedJob.error}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
